import React, { useState, useEffect } from 'react';
import {  Pencil, Trash2 } from 'lucide-react';
import adminBlockSlotService from '../../services/adminBlockSlotService';

const RESOURCE_TYPES = ['USER', 'ASSET', 'LOCATION', 'BRANCH'];

const emptyBlockForm = { name: '', messageCount: '', price: '', displayOrder: 0, isActive: true };
const emptySlotForm  = { name: '', resourceType: 'USER', quantity: '', price: '', displayOrder: 0, isActive: true };

export default function BlockSlotConfig() {
  const [activeTab, setActiveTab]     = useState('blocks');
  const [blockDefs, setBlockDefs]     = useState([]);
  const [slotDefs, setSlotDefs]       = useState([]);
  const [slotFilter, setSlotFilter]   = useState('');
  const [blockForm, setBlockForm]     = useState(emptyBlockForm);
  const [slotForm, setSlotForm]       = useState(emptySlotForm);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [editingSlotId, setEditingSlotId]   = useState(null);
  const [loading, setLoading]         = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    const [blocks, slots] = await Promise.all([
      adminBlockSlotService.getChatbotBlockDefs().catch(() => []),
      adminBlockSlotService.getCmmsSlotPackDefs().catch(() => []),
    ]);
    setBlockDefs(blocks || []);
    setSlotDefs(slots || []);
  };

  // Block handlers

  const handleSaveBlock = async () => {
    if (!blockForm.name || !blockForm.messageCount || !blockForm.price) return;
    setLoading(true);
    try {
      const payload = {
        name: blockForm.name,
        messageCount: parseInt(blockForm.messageCount),
        price: parseFloat(blockForm.price),
        displayOrder: parseInt(blockForm.displayOrder) || 0,
        isActive: blockForm.isActive,
      };
      if (editingBlockId) {
        await adminBlockSlotService.updateChatbotBlockDef(editingBlockId, payload);
      } else {
        await adminBlockSlotService.createChatbotBlockDef(payload);
      }
      setBlockForm(emptyBlockForm);
      setEditingBlockId(null);
      await loadAll();
    } finally {
      setLoading(false);
    }
  };

  const handleEditBlock = (def) => {
    setBlockForm({ name: def.name, messageCount: def.messageCount, price: def.price,
                   displayOrder: def.displayOrder, isActive: def.isActive });
    setEditingBlockId(def.id);
  };

  const handleDeleteBlock = async (id) => {
    if (!window.confirm('Delete this block definition?')) return;
    await adminBlockSlotService.deleteChatbotBlockDef(id);
    await loadAll();
  };

  // Slot handlers

  const handleSaveSlot = async () => {
    if (!slotForm.name || !slotForm.quantity || !slotForm.price) return;
    setLoading(true);
    try {
      const payload = {
        name: slotForm.name,
        resourceType: slotForm.resourceType,
        quantity: parseInt(slotForm.quantity),
        price: parseFloat(slotForm.price),
        displayOrder: parseInt(slotForm.displayOrder) || 0,
        isActive: slotForm.isActive,
      };
      if (editingSlotId) {
        await adminBlockSlotService.updateCmmsSlotPackDef(editingSlotId, payload);
      } else {
        await adminBlockSlotService.createCmmsSlotPackDef(payload);
      }
      setSlotForm(emptySlotForm);
      setEditingSlotId(null);
      await loadAll();
    } finally {
      setLoading(false);
    }
  };

  const handleEditSlot = (def) => {
    setSlotForm({ name: def.name, resourceType: def.resourceType, quantity: def.quantity,
                  price: def.price, displayOrder: def.displayOrder, isActive: def.isActive });
    setEditingSlotId(def.id);
  };

  const handleDeleteSlot = async (id) => {
    if (!window.confirm('Delete this slot pack definition?')) return;
    await adminBlockSlotService.deleteCmmsSlotPackDef(id);
    await loadAll();
  };

  const filteredSlots = slotFilter
    ? slotDefs.filter(s => s.resourceType === slotFilter)
    : slotDefs;

  return (
    <div className="space-y-6">
      {/* Tab switcher */}
      <div className="flex gap-2 border-b border-gray-200">
        {['blocks', 'slots'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'blocks' ? 'Chatbot Message Blocks' : 'CMMS Slot Packs'}
          </button>
        ))}
      </div>

      {/* Chatbot block defs */}
      {activeTab === 'blocks' && (
        <div>
          <h4 className="font-medium text-gray-700 mb-3">
            {editingBlockId ? 'Edit Block Definition' : 'Add Block Definition'}
          </h4>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <input className="border rounded px-3 py-2 text-sm" placeholder="Name (e.g. Starter Block)"
              value={blockForm.name} onChange={e => setBlockForm(f => ({ ...f, name: e.target.value }))} />
            <input className="border rounded px-3 py-2 text-sm" placeholder="Messages (e.g. 1000)" type="number"
              value={blockForm.messageCount} onChange={e => setBlockForm(f => ({ ...f, messageCount: e.target.value }))} />
            <input className="border rounded px-3 py-2 text-sm" placeholder="Price (e.g. 5.00)" type="number" step="0.01"
              value={blockForm.price} onChange={e => setBlockForm(f => ({ ...f, price: e.target.value }))} />
            <input className="border rounded px-3 py-2 text-sm" placeholder="Display order" type="number"
              value={blockForm.displayOrder} onChange={e => setBlockForm(f => ({ ...f, displayOrder: e.target.value }))} />
          </div>
          <div className="flex gap-2 mb-6">
            <button onClick={handleSaveBlock} disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 disabled:opacity-50">
              {editingBlockId ? 'Update' : 'Add Block'}
            </button>
            {editingBlockId && (
              <button onClick={() => { setBlockForm(emptyBlockForm); setEditingBlockId(null); }}
                className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50">
                Cancel
              </button>
            )}
          </div>

          <table className="w-full text-sm border border-gray-200 rounded">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Messages', 'Price', 'Order', 'Active', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-2 text-left font-medium text-gray-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {blockDefs.map(def => (
                <tr key={def.id} className="border-t border-gray-100">
                  <td className="px-4 py-2">{def.name}</td>
                  <td className="px-4 py-2">{def.messageCount?.toLocaleString()}</td>
                  <td className="px-4 py-2">${def.price}</td>
                  <td className="px-4 py-2">{def.displayOrder}</td>
                  <td className="px-4 py-2">{def.isActive ? '✓' : '—'}</td>
                  <td className="px-4 py-2 flex gap-2">
                    <button onClick={() => handleEditBlock(def)} className="text-blue-600 hover:text-blue-800"><Pencil size={14} /></button>
                    <button onClick={() => handleDeleteBlock(def.id)} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {blockDefs.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-4 text-center text-gray-400">No block definitions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* CMMS slot pack defs */}
      {activeTab === 'slots' && (
        <div>
          <div className="flex gap-2 mb-4">
            <select className="border rounded px-3 py-2 text-sm"
              value={slotFilter} onChange={e => setSlotFilter(e.target.value)}>
              <option value="">All resource types</option>
              {RESOURCE_TYPES.map(rt => <option key={rt} value={rt}>{rt}</option>)}
            </select>
          </div>

          <h4 className="font-medium text-gray-700 mb-3">
            {editingSlotId ? 'Edit Slot Pack' : 'Add Slot Pack'}
          </h4>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <input className="border rounded px-3 py-2 text-sm" placeholder="Name (e.g. +3 Users Pack)"
              value={slotForm.name} onChange={e => setSlotForm(f => ({ ...f, name: e.target.value }))} />
            <select className="border rounded px-3 py-2 text-sm"
              value={slotForm.resourceType} onChange={e => setSlotForm(f => ({ ...f, resourceType: e.target.value }))}>
              {RESOURCE_TYPES.map(rt => <option key={rt} value={rt}>{rt}</option>)}
            </select>
            <input className="border rounded px-3 py-2 text-sm" placeholder="Quantity (e.g. 3)" type="number"
              value={slotForm.quantity} onChange={e => setSlotForm(f => ({ ...f, quantity: e.target.value }))} />
            <input className="border rounded px-3 py-2 text-sm" placeholder="Price/period (e.g. 25.00)" type="number" step="0.01"
              value={slotForm.price} onChange={e => setSlotForm(f => ({ ...f, price: e.target.value }))} />
            <input className="border rounded px-3 py-2 text-sm" placeholder="Display order" type="number"
              value={slotForm.displayOrder} onChange={e => setSlotForm(f => ({ ...f, displayOrder: e.target.value }))} />
          </div>
          <div className="flex gap-2 mb-6">
            <button onClick={handleSaveSlot} disabled={loading}
              className="px-4 py-2 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:opacity-50">
              {editingSlotId ? 'Update' : 'Add Slot Pack'}
            </button>
            {editingSlotId && (
              <button onClick={() => { setSlotForm(emptySlotForm); setEditingSlotId(null); }}
                className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50">
                Cancel
              </button>
            )}
          </div>

          <table className="w-full text-sm border border-gray-200 rounded">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Type', 'Qty', 'Price', 'Order', 'Active', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-2 text-left font-medium text-gray-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredSlots.map(def => (
                <tr key={def.id} className="border-t border-gray-100">
                  <td className="px-4 py-2">{def.name}</td>
                  <td className="px-4 py-2">{def.resourceType}</td>
                  <td className="px-4 py-2">+{def.quantity}</td>
                  <td className="px-4 py-2">${def.price}</td>
                  <td className="px-4 py-2">{def.displayOrder}</td>
                  <td className="px-4 py-2">{def.isActive ? '✓' : '—'}</td>
                  <td className="px-4 py-2 flex gap-2">
                    <button onClick={() => handleEditSlot(def)} className="text-blue-600 hover:text-blue-800"><Pencil size={14} /></button>
                    <button onClick={() => handleDeleteSlot(def.id)} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {filteredSlots.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-4 text-center text-gray-400">No slot pack definitions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
